import React, { useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext, IntlContext, ListContext } from "context";
import { AdInterface } from "types/ad";
import { Role } from "types/user";
import { Button } from "components/ui/button";
import { Heart } from "lucide-react";
import { toggleFav, profileKeys } from "hooks/profile";
import { adKeys } from "hooks/ad";
import { cn } from "lib/utils";

interface Props {
    ad: AdInterface;
    className?: string;
    size?: "default" | "sm";
}

const FavoriteAdButton: React.FC<Props> = ({ ad, className, size = "default" }) => {
    const intl = useContext(IntlContext);
    const { account, currentRole, isLoggedIn } = useContext(AuthContext);
    const { refreshData } = useContext(ListContext);
    const queryClient = useQueryClient();
    const providerId = account?.resources?.provider?.[0]?.id;
    const isOwnAd = Boolean(account?.user?.id && ad.client?.user_id === account.user.id);
    const [isPending, setIsPending] = useState(false);
    const [isFavorite, setIsFavorite] = useState(
        () => providerId != null && ad.providers_fav?.includes(providerId),
    );

    if (!isLoggedIn || currentRole !== Role.PROVIDER || isOwnAd || !providerId) {
        return null;
    }

    const title = isFavorite
        ? intl.formatMessage("ad.favorite.remove_title")
        : intl.formatMessage("ad.favorite.add_title");

    const handleToggle = async (event?: React.MouseEvent) => {
        event?.stopPropagation();
        event?.preventDefault();

        if (isPending) {
            return;
        }

        setIsPending(true);
        try {
            const result = await toggleFav(ad.id);
            setIsFavorite(result.is_favorite);
            await queryClient.invalidateQueries({ queryKey: adKeys.ourDetail(ad.id) });
            await queryClient.invalidateQueries({ queryKey: profileKeys.favorites() });
            refreshData();
        } catch {
            // no-op
        } finally {
            setIsPending(false);
        }
    };

    const iconClass = size === "sm" ? "h-4 w-4" : "h-5 w-5";
    const buttonClass =
        size === "sm"
            ? "h-9 w-9 rounded-xl"
            : "w-12 h-12 rounded-[14px] bg-slate-50 border border-[#f1f5f9] hover:bg-[#f1f5f9]";

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
                "flex items-center justify-center transition",
                buttonClass,
                isFavorite ? "text-red-500" : "text-[#5a607c]",
                className,
            )}
            title={title}
            aria-label={title}
            aria-pressed={isFavorite}
            disabled={isPending}
            onClick={(event) => void handleToggle(event)}
        >
            <Heart aria-hidden="true" className={cn(iconClass, isFavorite && "fill-current")} />
        </Button>
    );
};

export default FavoriteAdButton;
